import type { FilterField, FilterCondition } from '@vnetwork/ui/data';
import type { CustomerServiceRow, ServiceSla } from '@/api/customer-sla';
import { humanize } from '../status-tones';

/** One row of the Service SLAs table (service + its resolved SLA, if any). */
export interface SlaRow {
  service: CustomerServiceRow;
  sla: ServiceSla | null;
}

/** '24-7' | 'business-hours' for the Coverage filter/value. */
function coverageKey(sla: ServiceSla | null): '24-7' | 'business-hours' | null {
  if (!sla) return null;
  return sla.effective_business_hours_only ? 'business-hours' : '24-7';
}

/** Distinct, sorted values from rows via an accessor (drops empties). */
function distinct<T>(rows: SlaRow[], pick: (r: SlaRow) => T | undefined | null): T[] {
  const set = new Set<T>();
  for (const r of rows) {
    const v = pick(r);
    if (v != null && v !== '') set.add(v);
  }
  return Array.from(set).sort();
}

/**
 * FilterBuilder fields for the Service SLAs table, derived from the loaded rows
 * so options never go stale: Status (service_status) · Tier (SLA template tier) ·
 * Coverage (24/7 vs business hours) · SLA (configured vs not). All applied
 * client-side (the SLA data is fanned out per service on the client).
 */
export function buildSlaFilterFields(rows: SlaRow[]): FilterField[] {
  const fields: FilterField[] = [];

  const statuses = distinct(rows, (r) => r.service.service_status);
  if (statuses.length > 1) {
    fields.push({
      value: 'service_status',
      label: 'Status',
      input: 'select',
      options: statuses.map((s) => ({ value: s, label: humanize(s) })),
    });
  }

  const tiers = distinct(rows, (r) => r.sla?.template_tier?.sla_tier);
  if (tiers.length > 0) {
    fields.push({
      value: 'sla_tier',
      label: 'SLA Tier',
      input: 'select',
      options: tiers.map((t) => ({ value: t, label: humanize(t) })),
    });
  }

  fields.push({
    value: 'coverage',
    label: 'Coverage',
    input: 'select',
    options: [
      { value: '24-7', label: '24/7' },
      { value: 'business-hours', label: 'Business hours' },
    ],
  });

  fields.push({
    value: 'sla_state',
    label: 'SLA',
    input: 'select',
    options: [
      { value: 'configured', label: 'With SLA' },
      { value: 'unconfigured', label: 'Without SLA' },
    ],
  });

  return fields;
}

/** True if the row satisfies every applied filter condition (values are OR within a field). */
export function matchesSlaConditions(row: SlaRow, conditions: FilterCondition[]): boolean {
  for (const c of conditions) {
    const values = c.value.split(',').map((v) => v.trim()).filter(Boolean);
    if (values.length === 0) continue;
    let actual: string | null;
    switch (c.field) {
      case 'service_status':
        actual = row.service.service_status ?? null;
        break;
      case 'sla_tier':
        actual = row.sla?.template_tier?.sla_tier ?? null;
        break;
      case 'coverage':
        actual = coverageKey(row.sla);
        break;
      case 'sla_state':
        actual = row.sla ? 'configured' : 'unconfigured';
        break;
      default:
        actual = null;
    }
    if (actual == null || !values.includes(actual)) return false;
  }
  return true;
}
